import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Box,
    CircularProgress,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    TablePagination,
    TableSortLabel,
    InputAdornment,
    Tooltip
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    WhatsApp as WhatsAppIcon,
    Phone as PhoneIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import serviceProviderService from '../../services/serviceProviderService';

const ServiceProviderList = () => {
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openForm, setOpenForm] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [searchTerm, setSearchTerm] = useState('');

    // Paginación
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Ordenamiento
    const [orderBy, setOrderBy] = useState('name');
    const [order, setOrder] = useState('asc');

    const [formData, setFormData] = useState({
        name: '',
        service_type: '',
        cell_phone: '',
        fixed_phone: '',
        email: '',
        description: ''
    });

    useEffect(() => {
        loadProviders();
    }, []);

    const loadProviders = async () => {
        try {
            setLoading(true);
            const data = await serviceProviderService.getAll();
            setProviders(data.data || []);
        } catch (error) {
            console.error('Error al cargar proveedores:', error);
            setSnackbar({
                open: true,
                message: 'Error al cargar proveedores',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setSelectedProvider(null);
        setFormData({
            name: '',
            service_type: '',
            cell_phone: '',
            fixed_phone: '',
            email: '',
            description: ''
        });
        setOpenForm(true);
    };

    const handleEdit = (provider) => {
        setSelectedProvider(provider);
        setFormData({
            name: provider.name,
            service_type: provider.service_type || '',
            cell_phone: provider.cell_phone,
            fixed_phone: provider.fixed_phone || '',
            email: provider.email || '',
            description: provider.description || ''
        });
        setOpenForm(true);
    };

    const handleCloseForm = () => {
        setOpenForm(false);
        setSelectedProvider(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (selectedProvider) {
                await serviceProviderService.update(selectedProvider.id, formData);
                setSnackbar({
                    open: true,
                    message: 'Proveedor actualizado exitosamente',
                    severity: 'success',
                });
            } else {
                await serviceProviderService.create(formData);
                setSnackbar({
                    open: true,
                    message: 'Proveedor creado exitosamente',
                    severity: 'success',
                });
            }

            loadProviders();
            handleCloseForm();
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Error al guardar proveedor',
                severity: 'error',
            });
        }
    };

    const handleDelete = async (provider) => {
        if (window.confirm(`¿Estás seguro de eliminar a "${provider.name}"?`)) {
            try {
                await serviceProviderService.delete(provider.id);
                loadProviders();
                setSnackbar({
                    open: true,
                    message: 'Proveedor eliminado exitosamente',
                    severity: 'success',
                });
            } catch (error) {
                setSnackbar({
                    open: true,
                    message: error.response?.data?.message || 'Error al eliminar proveedor',
                    severity: 'error',
                });
            }
        }
    };

    // Filtrado
    const filteredProviders = providers.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.service_type && p.service_type.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Ordenamiento
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedProviders = React.useMemo(() => {
        const comparator = (a, b) => {
            if (b[orderBy] < a[orderBy]) {
                return order === 'asc' ? 1 : -1;
            }
            if (b[orderBy] > a[orderBy]) {
                return order === 'asc' ? -1 : 1;
            }
            return 0;
        };
        return [...filteredProviders].sort(comparator);
    }, [filteredProviders, order, orderBy]);

    // Paginación
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedProviders = sortedProviders.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const openWhatsApp = (phone) => {
        if (!phone) return;
        // Limpiar número
        const cleanPhone = phone.replace(/[^0-9]/g, '');
        window.open(`https://wa.me/${cleanPhone}`, '_blank');
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Encabezado */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4">
                        Proveedores de Servicios
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Total: {filteredProviders.length} contactos
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                >
                    Nuevo Contacto
                </Button>
            </Box>

            {/* Buscador */}
            <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center' }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Buscar por nombre o servicio..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Paper>

            {/* Loading */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Tabla */}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'name'}
                                            direction={orderBy === 'name' ? order : 'asc'}
                                            onClick={() => handleRequestSort('name')}
                                        >
                                            Nombre
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'service_type'}
                                            direction={orderBy === 'service_type' ? order : 'asc'}
                                            onClick={() => handleRequestSort('service_type')}
                                        >
                                            Servicio
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>Contacto</TableCell>
                                    <TableCell>Descripción</TableCell>
                                    <TableCell align="right">Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedProviders.map((provider) => (
                                    <TableRow key={provider.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {provider.name}
                                            </Typography>
                                            {provider.email && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, color: 'text.secondary' }}>
                                                    <EmailIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                                    <Typography variant="caption">{provider.email}</Typography>
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {provider.service_type && (
                                                <Typography variant="body2" sx={{
                                                    display: 'inline-block',
                                                    bgcolor: 'primary.light',
                                                    color: 'primary.contrastText',
                                                    px: 1,
                                                    py: 0.2,
                                                    borderRadius: 1,
                                                    fontSize: '0.75rem'
                                                }}>
                                                    {provider.service_type}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => openWhatsApp(provider.cell_phone)}>
                                                    <WhatsAppIcon sx={{ fontSize: 16, mr: 0.5, color: '#25D366' }} />
                                                    <Typography variant="body2">{provider.cell_phone}</Typography>
                                                </Box>
                                                {provider.fixed_phone && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                                                        <PhoneIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                                        <Typography variant="body2">{provider.fixed_phone}</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary" sx={{
                                                maxWidth: 200,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {provider.description}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Editar">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEdit(provider)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Eliminar">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDelete(provider)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {paginatedProviders.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                            <Typography color="text.secondary">
                                                {searchTerm ? 'No se encontraron resultados' : 'No hay proveedores registrados'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {/* Paginación */}
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={filteredProviders.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Filas:"
                        />
                    </TableContainer>
                </>
            )}

            {/* Formulario */}
            <Dialog open={openForm} onClose={handleCloseForm} maxWidth="sm" fullWidth>
                <form onSubmit={handleSubmit}>
                    <DialogTitle>
                        {selectedProvider ? 'Editar Contacto' : 'Nuevo Contacto'}
                    </DialogTitle>

                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <TextField
                                label="Nombre Completo / Empresa"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                fullWidth
                                autoFocus
                            />

                            <TextField
                                label="Tipo de Servicio"
                                value={formData.service_type}
                                onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                                fullWidth
                                placeholder="Ej: Plomero, Electricista, Jardinero"
                                helperText="Opcional. Ayuda a categorizar y buscar."
                            />

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label="Celular / WhatsApp"
                                    value={formData.cell_phone}
                                    onChange={(e) => setFormData({ ...formData, cell_phone: e.target.value })}
                                    required
                                    fullWidth
                                    placeholder="Ej: 6600-9999"
                                />
                                <TextField
                                    label="Teléfono Fijo"
                                    value={formData.fixed_phone}
                                    onChange={(e) => setFormData({ ...formData, fixed_phone: e.target.value })}
                                    fullWidth
                                />
                            </Box>

                            <TextField
                                label="Correo Electrónico"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                fullWidth
                            />

                            <TextField
                                label="Descripción / Notas"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Detalles adicionales..."
                            />
                        </Box>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={handleCloseForm}>
                            Cancelar
                        </Button>
                        <Button type="submit" variant="contained">
                            Guardar
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ServiceProviderList;
